/**
 * Which way round the building is lit.
 *
 * Dark is the default because it is the design this school was built and reviewed in, not because
 * of what the operating system happens to prefer. Light is a choice somebody makes, and once made
 * it is remembered — a preference that resets on every visit is not a preference.
 *
 * The theme lives on the root element as `data-theme`, which is where the stylesheet looks for it.
 * Nothing here knows any colours; it only says which set to use.
 */

export type Theme = 'dark' | 'light';

/**
 * The one name this preference has.
 *
 * Spelled a second time in index.html, which sets the theme before the first paint so a light
 * page does not flash dark on the way in. Those two are the only places that know it, and spelled
 * differently in one of them a saved preference silently stops being found.
 */
const KEY = 'lms_theme';

/**
 * The stored choice, or the default.
 *
 * Storage can throw rather than merely be empty — a browser with site data blocked raises on
 * access — and a theme is not worth a blank page, so anything unreadable means dark.
 */
export function storedTheme(): Theme {
  try {
    return localStorage.getItem(KEY) === 'light' ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

/**
 * Puts the theme on the page, and remembers it.
 *
 * The `color-scheme` meta is updated alongside the attribute so the browser's own furniture —
 * scrollbars, form controls, the ground behind the page — follows the theme instead of
 * contradicting it.
 */
export function applyTheme(theme: Theme): void {
  document.documentElement.dataset.theme = theme;

  const meta = document.querySelector('meta[name="color-scheme"]');
  if (meta) meta.setAttribute('content', theme);

  try {
    localStorage.setItem(KEY, theme);
  } catch {
    // A browser that will not remember still gets the theme it was asked for, for this visit.
  }
}
